/**
 * Canonical enumerations for the e-Tawan system.
 *
 * Everything in this file is consumed by three places: the React UI, the
 * Cloudflare edge functions, and the Firestore security-rules test suite.
 * If a value is added here it must also be handled in `state.ts` transitions
 * and in `firestore.rules` — the rules tests will fail otherwise, which is the
 * intended safety net.
 */
/* -------------------------------------------------------------------------- */
/* Roles                                                                      */
/* -------------------------------------------------------------------------- */
export const ROLES = [
    'secretary',
    'chief_engineer',
    'superintendent_engineer',
    'xen',
    'sdo',
    'sub_engineer',
    'zilladar',
    'patwari',
    'data_operator',
    'admin',
    'public_liaison',
];
/** Descending administrative seniority. Index 0 is the most senior. */
export const ROLE_SENIORITY = [
    'secretary',
    'chief_engineer',
    'superintendent_engineer',
    'xen',
    'sdo',
    'sub_engineer',
    'zilladar',
    'patwari',
    'data_operator',
];
/**
 * `admin` and `public_liaison` are deliberately absent from ROLE_SENIORITY.
 *
 * The web-app administrator is a SYSTEM administrator, not a super-user over
 * case content: they manage accounts, routing rules and master data and may
 * read the audit log, but they may not read case personal data (name, CNIC,
 * address) nor alter a decision or an amount. Keeping `admin` outside the
 * seniority ladder stops "is senior to" checks from accidentally granting it
 * case authority. See docs/RBAC_MATRIX.md.
 *
 * `public_liaison` — the Public Liaison Cell — is outside it for a different
 * reason. The Cell answers to the Secretary and holds the identity of every
 * citizen who reports theft, which makes it powerful in one specific way and
 * in no other: it may read who reported, and it may raise a de-identified
 * ticket to a Chief Engineer. It has no authority over a case, no jurisdiction
 * of its own, and cannot decide, void or price anything. Putting it on the
 * engineering ladder would let a seniority comparison hand it case authority
 * it was never meant to have.
 */
export function isSeniorTo(a, b) {
    const ia = ROLE_SENIORITY.indexOf(a);
    const ib = ROLE_SENIORITY.indexOf(b);
    if (ia === -1 || ib === -1)
        return false;
    return ia < ib;
}
/** Roles that work primarily in the field on low-end phones — see Field theme (D12). */
export const FIELD_ROLES = [
    'sub_engineer',
    'patwari',
    'data_operator',
    'zilladar',
];
/** Roles that get the dark navy executive "Command" theme. */
export const COMMAND_ROLES = [
    'secretary',
    'chief_engineer',
    'superintendent_engineer',
    'xen',
    'sdo',
    'admin',
    'public_liaison',
];
/* -------------------------------------------------------------------------- */
/* Jurisdiction                                                               */
/* -------------------------------------------------------------------------- */
export const JURISDICTION_LEVELS = [
    'province',
    'zone',
    'circle',
    'division',
    'subdivision',
];
/** The level at which each role's read scope is anchored. */
export const ROLE_SCOPE = {
    // The Cell takes reports from the whole province, because a citizen does not
    // know which zone a canal belongs to and should not have to. Province scope
    // here means the REPORT intake only — it carries no case access at all.
    public_liaison: 'province',
    secretary: 'province',
    chief_engineer: 'zone',
    superintendent_engineer: 'circle',
    xen: 'division',
    sdo: 'subdivision',
    sub_engineer: 'subdivision',
    zilladar: 'subdivision',
    patwari: 'subdivision',
    data_operator: 'subdivision',
    admin: 'province',
};
/* -------------------------------------------------------------------------- */
/* Wire (immediate theft notification)                                        */
/* -------------------------------------------------------------------------- */
export const WIRE_STATES = [
    'DRAFT',
    'DISPATCHED',
    'SDO_CONFIRMED',
    'REJECTED',
    'CONVERTED_TO_TAWAN',
];
export const THEFT_TYPES = [
    'cut_in_bank',
    'pipe_installation',
    'crest_tampering',
    'diesel_pump',
    'siphon',
    'gate_tampering',
    'unauthorised_outlet',
    'other',
];
export const CANAL_BANKS = ['left', 'right'];
/* -------------------------------------------------------------------------- */
/* Tawan case                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * ONE canonical state field. The advisor's blueprint spread this across
 * wire.status + decisionStatus + isRecovered + hasStayOrder, which allowed
 * mutually contradictory combinations (e.g. "decided" + "not yet framed").
 */
export const CASE_STATES = [
    'FRAMED',
    'UNDER_VERIFICATION',
    // Countersigned by the Zilladar and put up to the SDO. Distinct from
    // HEARING_SCHEDULED because countersigning the docket and fixing a hearing
    // are two different acts by two different officers.
    'VERIFIED',
    'HEARING_SCHEDULED',
    'DECIDED',
    'RECOVERY_PENDING',
    'PARTIALLY_RECOVERED',
    'FULLY_RECOVERED',
    'ARREARS_REFERRED',
    'UNDER_APPEAL',
    'STAYED',
    'VOID',
];
/** States counted as administratively pending. See docs/DATA_DICTIONARY.md. */
export const PENDING_STATES = [
    'FRAMED',
    'UNDER_VERIFICATION',
    'VERIFIED',
    'HEARING_SCHEDULED',
    'UNDER_APPEAL',
];
/**
 * STAYED is NOT in PENDING_STATES.
 *
 * A case held up by a court stay order is not an administrative delay, and
 * counting it as one unfairly penalises the officer and corrupts the pendency
 * KPI. This was one of the advisor's better catches; this constant is where
 * that catch becomes enforceable rather than aspirational.
 */
export const STAYED_STATES = ['STAYED'];
export const DECISION_OUTCOMES = [
    'FINE_IMPOSED',
    'FIR_REGISTERED',
    'CONNECTION_DISCONNECTED',
    'METER_SEALED',
    'WATER_RELEASED',
    'WARNING',
    'EXONERATED',
];
/** Outcomes that create a recoverable money demand. */
export const FINE_BEARING_OUTCOMES = ['FINE_IMPOSED', 'FIR_REGISTERED'];
export const APPEAL_OUTCOMES = ['UPHELD', 'MODIFIED', 'SET_ASIDE', 'REMANDED'];
export const CROPS = [
    'wheat',
    'cotton',
    'sugarcane',
    'rice',
    'maize',
    'fodder',
    'orchard',
    'vegetables',
    'other',
];
/* -------------------------------------------------------------------------- */
/* Public whistleblower report (RDTB)                                         */
/* -------------------------------------------------------------------------- */
/**
 * The life of a citizen's report, from the form to a case or a dismissal.
 *
 * The chain is deliberately long, because each hop is a person taking
 * responsibility for passing it on: the Cell vouches that a real person filed
 * it, the Chief Engineer accepts it into a zone, and the SDO investigates it.
 * A shorter chain would mean somebody was not answerable for a step.
 *
 * Every state after FORWARDED is reachable by officers who can see only a
 * de-identified ticket. See publicReport.ts for the boundary and why it is a
 * subcollection rather than a set of hidden fields.
 */
export const PUBLIC_REPORT_STATES = [
    /** Filed by a citizen. Nobody has looked at it yet. */
    'SUBMITTED',
    /** The Cell telephoned the reporter and satisfied itself they are real. */
    'IDENTITY_VERIFIED',
    /** The Cell raised a de-identified ticket to the zone's Chief Engineer. */
    'FORWARDED',
    /** The Chief Engineer took it into their zone. */
    'ACCEPTED',
    /** The Chief Engineer assigned it to a sub-division. */
    'WITH_SDO',
    /** The SDO's team is on the ground. */
    'UNDER_INVESTIGATION',
    /** Theft confirmed; it became a wire and then a case. */
    'CONVERTED_TO_WIRE',
    /** Investigated, nothing found. Not the same as fabricated. */
    'CLOSED_NO_THEFT',
    /**
     * The SDO found the report fabricated, and cited a site-visit video.
     *
     * Distinct from CLOSED_NO_THEFT on purpose. "Nothing found" costs the
     * reporter nothing; "fabricated" follows them to their next report, so it
     * has to be a separate, deliberate act with evidence behind it.
     */
    'MARKED_FAKE',
    /** The Chief Engineer refused it; back with the Cell. */
    'RETURNED_TO_CELL',
    /** The SDO found it belongs elsewhere; back with the Chief Engineer. */
    'RETURNED_TO_ZONE',
    /** The Cell closed it without forwarding — unreachable reporter, say. */
    'DISMISSED_BY_CELL',
];
/** Report states from which nothing further happens. */
export const TERMINAL_REPORT_STATES = [
    'CONVERTED_TO_WIRE',
    'CLOSED_NO_THEFT',
    'MARKED_FAKE',
    'DISMISSED_BY_CELL',
];
/**
 * States in which an officer outside the Cell may see the ticket at all.
 *
 * Before FORWARDED the report is the Cell's alone: it still has an unverified
 * reporter attached to it and has not been vouched for by anybody.
 */
export const FORWARDED_REPORT_STATES = [
    'FORWARDED',
    'ACCEPTED',
    'WITH_SDO',
    'UNDER_INVESTIGATION',
    'CONVERTED_TO_WIRE',
    'CLOSED_NO_THEFT',
    'MARKED_FAKE',
    'RETURNED_TO_ZONE',
];
/**
 * Allow-listed video platforms. A submitted URL is parsed into
 * {platform, videoId} and the raw URL is discarded — we never store or
 * server-fetch an arbitrary user-supplied URL (SSRF). See lib video parser.
 */
export const VIDEO_PLATFORMS = ['youtube', 'facebook', 'x', 'instagram', 'tiktok'];
/** Roles a public report may be routed to, configured by the web-app admin. */
export const ROUTABLE_ROLES = [
    'xen',
    'superintendent_engineer',
    'chief_engineer',
    'secretary',
];
/* -------------------------------------------------------------------------- */
/* Escalation                                                                 */
/* -------------------------------------------------------------------------- */
export const ESCALATION_LEVELS = ['NONE', 'AMBER_XEN', 'AMBER_LOGGED', 'RED_CE_SECRETARY'];
/**
 * Default SLA thresholds in hours, measured from wire dispatch.
 * Configurable per division — see recommendation R3 in the executor plan:
 * an escalation that surprises an officer breeds resistance, so the officer
 * sees the countdown before it fires.
 */
export const DEFAULT_SLA_HOURS = {
    sdoActionDue: 48,
    amberEscalation: 72,
    redEscalation: 24 * 7,
};
//# sourceMappingURL=enums.js.map